import { RRule } from 'rrule'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Schedule } from '@/core/api/entities/schedule'

// Parse VEVENT to extract DTSTART/DTEND/DURATION/RRULE
const parseVEvent = (vevent: string) => {
	const lines = vevent.split('\n')
	let dtstart = ''
	let dtend = ''
	let duration = ''
	let rrule = ''

	for (const line of lines) {
		if (line.startsWith('DTSTART:')) dtstart = line.slice('DTSTART:'.length)
		else if (line.startsWith('DTEND:')) dtend = line.slice('DTEND:'.length)
		else if (line.startsWith('DURATION:'))
			duration = line.slice('DURATION:'.length)
		else if (line.startsWith('RRULE:')) rrule = line.slice('RRULE:'.length)
	}

	return { dtstart, dtend, duration, rrule }
}

// The backend VEVENT values are stored in a compact format like 20260128T083000Z
// In other parts of the codebase (timeslotConverter) we treat these as local times by stripping Z.
const parseCompactDateToLocal = (value: string): Date | null => {
	if (!value) return null
	const clean = value.replace(/Z$/, '')
	if (clean.length < 15) return null

	const year = parseInt(clean.substring(0, 4))
	const month = parseInt(clean.substring(4, 6)) - 1
	const day = parseInt(clean.substring(6, 8))
	const hour = parseInt(clean.substring(9, 11))
	const minute = parseInt(clean.substring(11, 13))
	const second = parseInt(clean.substring(13, 15))

	return new Date(year, month, day, hour, minute, second)
}

const parseDurationToMs = (duration: string): number => {
	// Supported formats in our schedules: PT#H, PT#M, PT#H#M
	const hourMatch = duration.match(/(\d+)H/)
	const minuteMatch = duration.match(/(\d+)M/)
	const hours = hourMatch ? parseInt(hourMatch[1]) : 0
	const minutes = minuteMatch ? parseInt(minuteMatch[1]) : 0
	return (hours * 60 + minutes) * 60 * 1000
}

const getTimeslotDurationMs = (
	start: Date,
	dtend: string,
	duration: string
) => {
	const end = parseCompactDateToLocal(dtend)
	if (end) {
		return Math.max(0, end.getTime() - start.getTime())
	}
	if (duration) {
		return parseDurationToMs(duration)
	}
	// fallback: 0
	return 0
}

const getFirstPlaylistFromTimeslot = (
	schedule: Schedule,
	timeslotId: number
) => {
	const timeslot = schedule.timeslots?.find((t) => t.id === timeslotId)
	const playlists = timeslot?.timeslotContents?.[0]?.playlists
	return playlists && playlists.length > 0 ? playlists[0] : null
}

/**
 * Picks the playlist that should play "now" in the user's local timezone, based on schedule.timeslots[].vevent.
 * If no active timeslot is found, falls back to the first available playlist in any timeslot.
 */
export const getActivePlaylistForSchedule = (
	schedule: Schedule,
	now: Date = new Date(),
	options: { fallbackToFirstPlaylist?: boolean } = {}
): Playlist | null => {
	const { fallbackToFirstPlaylist = true } = options
	const timeslots = schedule.timeslots || []

	// Try to find active slot(s)
	type ActiveMatch = { timeslotId: number; priority: number }
	const active: ActiveMatch[] = []

	for (const timeslot of timeslots) {
		const { dtstart, dtend, duration, rrule } = parseVEvent(timeslot.vevent)
		const start = parseCompactDateToLocal(dtstart)
		if (!start) continue

		const durationMs = getTimeslotDurationMs(start, dtend, duration)
		if (durationMs <= 0) continue

		if (rrule) {
			try {
				// Use DTSTART as the base start for the RRULE.
				// NOTE: this repo overrides rrule typings in src/core/types/rrule.d.ts,
				// so we keep the type assertions local to this utility (no global type changes).
				type ParsedRRule = { origOptions: any }
				type RRuleWithBefore = RRule & {
					before: (dt: Date, inc?: boolean) => Date | null
				}

				const parsed = RRule.fromString(
					`RRULE:${rrule}`
				) as unknown as ParsedRRule
				const rule = new RRule({
					...parsed.origOptions,
					dtstart: start,
				}) as RRuleWithBefore
				const last = rule.before(now, true)
				if (!last) continue

				const startMs = last.getTime()
				const endMs = startMs + durationMs
				if (now.getTime() >= startMs && now.getTime() < endMs) {
					active.push({ timeslotId: timeslot.id, priority: timeslot.priority })
				}
			} catch {
				// If RRULE parsing fails, ignore and continue
			}
		} else {
			const startMs = start.getTime()
			const endMs = startMs + durationMs
			if (now.getTime() >= startMs && now.getTime() < endMs) {
				active.push({ timeslotId: timeslot.id, priority: timeslot.priority })
			}
		}
	}

	if (active.length > 0) {
		// Prefer the highest priority
		active.sort((a, b) => (b.priority || 0) - (a.priority || 0))
		const playlist = getFirstPlaylistFromTimeslot(
			schedule,
			active[0].timeslotId
		)
		if (playlist) return playlist
	}

	if (!fallbackToFirstPlaylist) return null

	// Fallback: first available playlist in any timeslot
	for (const timeslot of timeslots) {
		const playlists = timeslot?.timeslotContents?.[0]?.playlists
		if (playlists && playlists.length > 0) return playlists[0]
	}

	return null
}
